<template>
  <div class="container-bottomnav">
    <div class="floting-wrap" ref="interpreterOptionsRef" @click="toggleFloatingMenu">
      <button class="button-icon floting-btn">
        <AnimationAiWhite />
      </button>

      <FloatingMenu v-if="isFloatingMenuOpen" :is-closing="isClosing" />
    </div>

    <teleport to="body">
      <!--일반메뉴-->
      <DropdownBottom v-if="currentMenu === 'menu'" :target-el="menuOptionsRef" @close="handleMenu()">
        <div class="container-menu">
          <template v-for="(menu, menuIndex) in menuOptions" :key="`self_service_menu_${menuIndex}`">
            <button
              v-if="isVisibleMenu(menu)"
              class="button-big button-white button-full-width-left"
              @click="onClickMenuOption(menu)"
            >
              <component :is="menu.iconComponent" />
              {{ t(menu.titleKey) }}
            </button>
          </template>
        </div>
      </DropdownBottom>

      <!--통역메뉴-->
      <DropdownBottom v-if="currentMenu === 'interpreter'" :target-el="interpreterOptionsRef" @close="handleMenu()">
        <div class="container-menu">
          <button
            v-for="(interpreterMenu, interpreterMenuIndex) in interpreterOptions"
            :key="`interpreter_${interpreterMenuIndex}`"
            class="button-big button-white button-full-width-left"
            @click="onClickMenuOption(interpreterMenu)"
          >
            <component :is="interpreterMenu.iconComponent" />
            {{ t(interpreterMenu.titleKey) }}
          </button>
        </div>
      </DropdownBottom>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import AnimationAiWhite from '@/components/jh-app/icons/AnimationAiWhite.vue';
import DropdownBottom from '@/components/shared/base/DropdownBottom.vue';
import FloatingMenu from '@/components/shared/base/FloatingMenu.vue';
import { useModalCreditHistory } from '@/composables/domains/self-service/useModalCreditHistory';
import { useAdminStore } from '@/stores/admin';
import { useSubscriptionStore } from '@/stores/subscription';
import { useUserStore } from '@/stores/user';
import { TypeUser } from '@/types/common';
import { ISelfServiceMenuItem } from '@/types/components';
import { SELF_SERVICE_DROPDOWN_MENU, SELF_SERVICE_MENU } from '@/utils/constants/selfService';

withDefaults(
  defineProps<{
    domain?: 'mobile' | 'self-service';
  }>(),
  {
    domain: 'mobile',
  }
);

const { t } = useI18n();
const router = useRouter();

const adminStore = useAdminStore();
const userStore = useUserStore();
const subscriptionStore = useSubscriptionStore();

const { openModalCreditHistory } = useModalCreditHistory();

const isFloatingMenuOpen = ref(false);
const isClosing = ref(false);
const isInitialAnimationLoad = ref<boolean>(true);
const currentMenu = ref<string>('');

const menuOptionsRef = ref<HTMLElement>(); // 하단바 Ref
const interpreterOptionsRef = ref<HTMLElement>(); // 하단바 Ref

const userType = computed<TypeUser>(() => {
  const { userType = '' } = userStore.userInfo || {};
  return userType;
});

const menuOptions = computed<ISelfServiceMenuItem[]>(() => {
  return [...getMenuList(['', 'label_settings']), ...SELF_SERVICE_DROPDOWN_MENU];
});

const interpreterOptions = computed<ISelfServiceMenuItem[]>(() => {
  return getMenuList(['label_interpretation']);
});

const getMenuList = (keys: string[]) => {
  return SELF_SERVICE_MENU.reduce((acc, menu) => {
    if (keys.includes(menu.titleKey)) {
      acc.push(...menu.menuItems);
    }
    return acc;
  }, []);
};

const isVisibleMenu = (item: ISelfServiceMenuItem): boolean => {
  return (
    (item.accessUserTypes.length === 0 || item.accessUserTypes.includes(userType.value)) &&
    (item.accessPlanTypes.length === 0 || item.accessPlanTypes.includes(subscriptionStore.currentPlan))
  );
};

const handleMenu = (type: string = '') => {
  currentMenu.value = type;
};

const navigateTo = (path: string, query: Record<string, string> = {}) => {
  if (path) {
    router.push({ path, query });
  }
};

const onClickLogout = async () => {
  await userStore.requestLogout();
  const isHost = adminStore.isHost;
  navigateTo('/login/ss', {
    partnerCode: isHost ? '' : adminStore.getPartnerInfo.partnerCode,
  });
};

// 메뉴 옵션 클릭 시
const onClickMenuOption = (menu: ISelfServiceMenuItem) => {
  switch (menu.titleKey) {
    case 'button_logout':
      onClickLogout();
      break;
    case 'credit_history':
      openModalCreditHistory();
      break;
    default:
      navigateTo(menu.routePath);
      handleMenu();
  }
};

const toggleFloatingMenu = () => {
  if (isFloatingMenuOpen.value) {
    // ✅ 닫힐 때 isClosing으로 open 클래스 먼저 제거 후 언마운트
    isClosing.value = true;
    setTimeout(() => {
      isFloatingMenuOpen.value = false;
      isClosing.value = false;
    }, 500); // transition 시간보다 약간 길게
  } else {
    isFloatingMenuOpen.value = true;
  }
};

onMounted(() => {
  setTimeout(() => {
    isInitialAnimationLoad.value = false;
  }, 3200);
});
</script>

<style scoped lang="scss">
.container-bottomnav {
  position: fixed;
  bottom: 12px;
  right: 12px;
  z-index: 48;
  .floting-btn {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: $color-gray-900;
    .animation-ai {
      display: flex;
      align-items: center;
      justify-content: center;
      :deep(svg) {
        width: 20px;
        height: 20px;
      }
    }
  }
}

.container-menu {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  background: var(--surface-default);
}

.logo {
  height: 2.5rem;
  border-radius: 100px;
}

.floting-wrap {
  position: relative;
}
</style>
